<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useToast } from '@ai/ui-components/toast'
import { useUserRoleOptions } from '@/composables/users/useUserRoleOptions'
import { useUpdateUserMutation } from '@/composables/queries'

import type { Ref } from 'vue'

// Interface for exposed properties (for testing)
export interface UpdateUserModalExposed {
  open: Ref<boolean>
  selectedRole: Ref<string>
  isSubmitting: Ref<boolean>
  showCancelConfirmation: Ref<boolean>
  hasUnsavedChanges: Ref<boolean>
  handleModalClose: (newValue: boolean) => void
  handleSubmit: () => Promise<boolean>
  handleCancel: () => boolean
  confirmDiscard: () => boolean
  keepEditing: () => boolean
}

export interface UserToUpdate {
  id: string
  email: string
  name?: string | null
  role: string
  type: 'user' | 'invitation'
}

const { t } = useI18n()
const toast = useToast()

const open = defineModel<boolean>('open', { default: false })

const props = defineProps<{
  user: UserToUpdate | null
}>()

const emit = defineEmits<{
  userUpdated: []
}>()

const { options: localizedRoleOptions } = useUserRoleOptions()
const selectedRole = ref('supervisor')
const showCancelConfirmation = ref(false)
const initialRole = ref('supervisor')

// Track if form has been modified
const hasUnsavedChanges = computed(() => {
  return selectedRole.value !== initialRole.value
})

const { mutateAsync, isPending: isSubmitting } = useUpdateUserMutation()

const handleModalClose = (newValue: boolean): void => {
  // If trying to close the modal and there are unsaved changes, show confirmation
  if (!newValue && open.value && hasUnsavedChanges.value) {
    showCancelConfirmation.value = true
    return
  }
  open.value = newValue
}

const handleCancel = (): boolean => {
  if (hasUnsavedChanges.value) {
    showCancelConfirmation.value = true
    return false // Prevent modal from closing
  }
  open.value = false
  return true
}

const confirmDiscard = (): boolean => {
  showCancelConfirmation.value = false
  open.value = false
  return true
}

const keepEditing = (): boolean => {
  showCancelConfirmation.value = false
  return true
}

const handleSubmit = async (): Promise<boolean> => {
  if (!props.user) {
    return false
  }

  try {
    await mutateAsync({
      id: props.user.id,
      role: selectedRole.value,
      type: props.user.type,
    })

    toast.success(t('changes_saved_successfully'))
    emit('userUpdated')
    open.value = false
    return true
  } catch {
    return false
  }
}

// Initialize form when modal opens or user changes
watch(
  () => [open.value, props.user],
  ([isOpen, user]) => {
    if (isOpen && user && typeof user !== 'boolean') {
      selectedRole.value = user.role
      initialRole.value = user.role
    }
  },
  { immediate: true },
)

// Expose properties for testing
defineExpose({
  open,
  selectedRole,
  isSubmitting,
  showCancelConfirmation,
  hasUnsavedChanges,
  handleModalClose,
  handleSubmit,
  handleCancel,
  confirmDiscard,
  keepEditing,
})
</script>

<template>
  <OModal
    :open="open"
    @update:open="handleModalClose"
    :title="t('edit_user')"
    :secondary-action-title="t('cancel')"
    :primary-action-title="t('save_changes')"
    :secondary-action-promise="handleCancel"
    primary-action-enabled
    secondary-action-enabled
    :primary-action-promise="handleSubmit"
    :primary-action-loading="isSubmitting"
    primary-action-color="success"
    closable
    size="medium"
  >
    <template #subtitle>
      <span class="typography-body-2-long">{{
        t('assign_role_permissions_based_on_access_needs')
      }}</span>
    </template>
    <template #content>
      <div class="flex flex-col space-y-4 text-neutral-1 pb-2">
        <div class="flex flex-col space-y-2">
          <label class="typography-body-2-strong">{{ t('email_address') }}</label>
          <OInput v-mp-no-track type="email" :model-value="user?.email || ''" disabled readonly />
        </div>
        <div v-if="user?.name" class="flex flex-col space-y-2">
          <label class="typography-body-2-strong">{{ t('name') }}</label>
          <OInput type="text" :model-value="user.name" disabled readonly />
        </div>
        <div class="flex flex-col space-y-2">
          <label class="typography-body-2-strong">{{ t('assign_role') }}</label>
          <ODropdown
            v-model="selectedRole"
            :options="localizedRoleOptions"
            medium
            :filter-title="t('role')"
            teleport
          />
        </div>
      </div>
    </template>
  </OModal>

  <!-- Unsaved changes confirmation modal -->
  <OModal
    v-model:open="showCancelConfirmation"
    :title="t('unsaved_changes')"
    :primary-action-title="t('discard_changes')"
    :secondary-action-title="t('continue')"
    :primary-action-promise="confirmDiscard"
    :secondary-action-promise="keepEditing"
    primary-action-enabled
    secondary-action-enabled
    danger
  >
    <template #content>
      <span class="text-neutral-1">{{ t('unsaved_changes_warning') }}</span>
    </template>
  </OModal>
</template>
